import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { type Case, caseResponseSchema } from "../types";
import { getPatientQueryKey } from "./usePatient";

const CASE_URL = `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/:workplaceId/cases/:caseId`;

export function getCaseQueryKey(params: { workplaceId: string; caseId?: string }) {
  return ["home-health", params.workplaceId, "cases", params.caseId];
}

export function useCase(caseId?: string, options: UseQueryOptions<Case, unknown, Case> = {}) {
  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: getCaseQueryKey({ workplaceId, caseId }),
    queryFn: async () => {
      const response = await get({
        url: CASE_URL.replace(":workplaceId", workplaceId).replace(":caseId", caseId!),
        responseSchema: caseResponseSchema,
      });

      return response.data.data;
    },
    retry: 0,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
    enabled: isDefined(caseId),
    onSuccess: (caseItem) => {
      queryClient.setQueryData(
        getPatientQueryKey({ workplaceId, patientId: caseItem.attributes.patient.id }),
        caseItem.attributes.patient
      );
    },
    ...options,
  });
}
